import { useRef, useCallback } from 'react';
import { useSelector } from '@xstate/store/react';
import { useCanvasDragHandlers } from '../../hooks/useCanvasDragHandlers';
import { useEntityData } from '../../stores/EntityDataContext';
import { canvasSelectionStore } from '../../stores/canvasSelectionStore';
import { useLockedLayerIds } from '../../hooks/useLockedLayerIds';

export function useGroupBoxInteraction(groupId: string, layerId: string, dataBorderColor?: string, dataBorderWidth?: number) {
  const { handleGroupDragStart } = useCanvasDragHandlers();
  const { handleEntityContextMenu, handleGroupToggleCollapse } = useEntityData();
  const lockedLayerIds = useLockedLayerIds();
  const isLocked = lockedLayerIds.has(layerId);

  const isSelected = useSelector(canvasSelectionStore, s => s.context.selectedEntityIds.includes(groupId));
  const isEntered = useSelector(canvasSelectionStore, s => s.context.enteredGroupId === groupId);

  const hasMoved = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return;
    if (isLocked) return;
    hasMoved.current = false;
    startPos.current = { x: e.clientX, y: e.clientY };

    const handlePointerMove = (ev: PointerEvent) => {
      const dx = Math.abs(ev.clientX - startPos.current.x);
      const dy = Math.abs(ev.clientY - startPos.current.y);
      if (dx + dy > 3 && !hasMoved.current) {
        hasMoved.current = true;
        document.body.style.cursor = 'grabbing';
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
        document.addEventListener('pointerup', () => { document.body.style.cursor = ''; }, { once: true });
        handleGroupDragStart(groupId, e);
      }
    };

    const handlePointerUp = () => {
      document.body.style.cursor = '';
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
  }, [groupId, isLocked, handleGroupDragStart]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (hasMoved.current) return;
    if (isLocked) return;
    if ((e.target as HTMLElement).closest('[data-group-collapse-btn]')) return;
    if (e.shiftKey) {
      canvasSelectionStore.send({ type: 'entity.toggle', entityId: groupId });
    } else {
      canvasSelectionStore.send({ type: 'entity.selectOne', entityId: groupId });
    }
  }, [groupId, isLocked]);

  const handleDoubleClick = useCallback((_e: React.MouseEvent) => {
    canvasSelectionStore.send({ type: 'group.enter', groupId });
  }, [groupId]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleEntityContextMenu(groupId, e);
  }, [groupId, handleEntityContextMenu]);

  const handleCollapseClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    handleGroupToggleCollapse(groupId);
  }, [groupId, handleGroupToggleCollapse]);

  const defaultBorderColor = dataBorderColor || 'rgba(148, 163, 184, 0.5)';
  const defaultBorderWidth = dataBorderColor ? (dataBorderWidth ?? 1) : 1;
  const borderColor = isEntered ? '#22c55e' : isSelected ? '#3b82f6' : defaultBorderColor;
  const borderWidth = isEntered ? 2 : isSelected ? 3 : defaultBorderWidth;

  return {
    isSelected,
    isEntered,
    borderColor,
    borderWidth,
    handlePointerDown,
    handleClick,
    handleDoubleClick,
    handleContextMenu,
    handleCollapseClick,
  };
}
